<template>
  <div class="pb-72">
    <div class="-ml-24 flex md:ml-0">
      <RevBreadcrumb :ariaLabel="i18n(translations.breadcrumb)" :breads />
    </div>

    <div class="mt-12 flex items-start md:mt-56 md:px-32">
      <div class="grow lg:mr-32">
        <h1 class="heading-1">{{ productData?.title }}</h1>
        <p v-if="productData?.productId" class="body-1 mt-16">
          {{
            i18n(translations.productId, { productId: productData.productId })
          }}
        </p>
        <div class="mt-32">
          <h2 class="heading-2">{{ i18n(translations.marketTitle) }}</h2>
          <div
            v-if="activeMarket"
            class="mt-16 grid grid-cols-2 gap-12 md:grid-cols-3"
          >
            <RevRadio
              v-for="market in productData?.availableMarkets"
              :id="market"
              :key="market"
              v-model="activeMarket"
              name="market"
              :value="market"
              variant="full"
            >
              <template #label>
                <div class="flex gap-4">
                  <Flag :country-code="market as MarketCountryCode" />
                  {{ market }}
                </div>
              </template>
            </RevRadio>
          </div>
        </div>
        <div class="mt-32">
          <div
            v-if="isLoadingQuotations"
            class="flex h-screen items-start justify-center"
          >
            <RevLoadingScreen class="m-32" text="" />
          </div>

          <div v-if="!isLoadingQuotations">
            <div class="flex">
              <h2 class="heading-2 grow">
                {{ i18n(translations.priceTitle) }}
              </h2>
              <RevButton
                class="md:hidden"
                size="small"
                variant="primary"
                @click="handleGetAllBackBoxes"
              >
                {{ i18n(translations.getAllBackboxes) }}
              </RevButton>
            </div>

            <div
              v-for="(quotation, index) in quotations"
              :key="quotation.grade.code"
              class="mt-16 flex flex-col md:mt-0 md:flex-row md:items-center"
            >
              <div class="flex md:w-1/5 md:flex-col">
                <div :class="getHeadingClass(index)">
                  <p class="body-1-bold">
                    {{ i18n(translations.grade) }}
                  </p>
                </div>
                <div
                  class="border-static-default-low flex h-96 items-center border-b-2 px-8"
                >
                  <RevTag :label="quotation.grade.name" variant="secondary" />
                </div>
              </div>
              <div class="flex md:w-1/5 md:flex-col">
                <div
                  :class="[
                    getHeadingClass(index),
                    'border-static-default-low border-b-2',
                  ]"
                >
                  <p class="body-1-bold">
                    {{ i18n(translations.backboxPrice) }}
                  </p>
                </div>
                <div
                  class="border-static-default-low flex h-96 items-center border-b-2 px-8"
                >
                  <p v-if="quotation.backboxPrice" class="body-1">
                    {{ i18n.price(quotation.backboxPrice) }}
                  </p>
                </div>
              </div>
              <div class="flex md:w-1/5 md:flex-col">
                <div
                  :class="[
                    getHeadingClass(index),
                    'border-static-default-low border-b-2',
                  ]"
                >
                  <p class="body-1-bold">
                    {{ i18n(translations.yourPrice) }}
                  </p>
                </div>
                <div
                  class="border-static-default-low flex h-96 items-center border-b-2 px-8"
                >
                  <RevInputText
                    :id="`${quotation.grade.code}-price`"
                    v-model="offer[quotation.grade.code].price"
                    data-test="quotation-input-price"
                    :label="i18n(translations.priceInputLabel, { currency })"
                    type="number"
                  />
                </div>
              </div>
              <div class="flex md:w-1/5 md:flex-col">
                <div
                  :class="[
                    getHeadingClass(index),
                    'border-static-default-low border-b-2',
                  ]"
                >
                  <p class="body-1-bold">
                    {{ i18n(translations.sku) }}
                  </p>
                </div>
                <div
                  class="border-static-default-low flex h-96 items-center border-b-2 px-8"
                >
                  <RevInputText
                    :id="`${quotation.grade.code}-sku`"
                    v-model="offer[quotation.grade.code].sku"
                    :label="i18n(translations.sku)"
                    type="text"
                  />
                </div>
              </div>
              <div class="flex md:w-1/5 md:flex-col">
                <div
                  :class="[
                    getHeadingClass(index),
                    'border-static-default-low border-b-2',
                  ]"
                >
                  <RevButton
                    class="hidden md:block"
                    size="small"
                    variant="primary"
                    @click="handleGetAllBackBoxes"
                  >
                    {{ i18n(translations.getAllBackboxes) }}
                  </RevButton>
                </div>

                <div
                  class="border-static-default-low flex h-96 items-center border-b-2 px-8"
                >
                  <RevButton
                    size="small"
                    variant="primary"
                    @click="handleGetBackbox(quotation)"
                  >
                    {{ i18n(translations.getBackbox) }}
                  </RevButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RevIllustration
        v-if="productData?.image"
        alt=""
        class="hidden lg:block"
        :height="440"
        :src="productData.image"
        :width="440"
      />
      <RevStickyBar>
        <div class="py-12 md:px-32">
          <div class="hidden md:flex">
            <RevButton
              class="mr-12"
              :disabled="isSavingListing"
              :loading="isSavingListingWithRedirect"
              variant="secondary"
              @click="saveListing({ redirect: true })"
            >
              {{ i18n(translations.saveListingCreate) }}
            </RevButton>
            <RevButton
              :disabled="isSavingListingWithRedirect"
              :loading="isSavingListing"
              variant="primary"
              @click="saveListing({ redirect: false })"
            >
              {{ i18n(translations.saveListing) }}
            </RevButton>
          </div>
          <div class="flex md:hidden">
            <RevButton
              class="mr-2"
              :disabled="isSavingListing"
              :loading="isSavingListingWithRedirect"
              size="small"
              variant="secondary"
              @click="saveListing({ redirect: true })"
            >
              {{ i18n(translations.saveListingCreateMobile) }}
            </RevButton>
            <RevButton
              :disabled="isSavingListingWithRedirect"
              :loading="isSavingListing"
              size="small"
              variant="primary"
              @click="saveListing({ redirect: false })"
            >
              {{ i18n(translations.saveListing) }}
            </RevButton>
          </div>
        </div>
      </RevStickyBar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import {
  getRefurbisherProduct,
  getRefurbisherProductListings,
  putRefurbisherQuotations,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import type {
  Offer,
  RefurbisherQuotationsListing,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import { LISTING_PUBLICATION_STATE } from '@backmarket/nuxt-layer-buyback/src/constants'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { getMarketplace } from '@backmarket/nuxt-module-marketplace/getMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { tw } from '@backmarket/utils/string/tw'
import { RevBreadcrumb } from '@ds/components/Breadcrumb'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevRadio } from '@ds/components/Radio'
import { RevStickyBar } from '@ds/components/StickyBar'
import { RevTag } from '@ds/components/Tag'

import { ROUTE_NAMES } from '~/constants/routes'
import { removeDecimalsFromString } from '~/scopes/buyback/utils/buyback.utils'

import translations from './CreateProductQuotations.translations'

const route = useRoute()
const i18n = useI18n()
const logger = useLogger()
const { openErrorToast, openSuccessToast } = useTheToast()

const quotations = ref<RefurbisherQuotationsListing[]>([])
const offer = ref<Offer>({})
const isLoadingQuotations = ref(false)
const isSavingListing = ref(false)
const isSavingListingWithRedirect = ref(false)

const { error: productDataError, data: productData } = await useHttpFetch(
  getRefurbisherProduct,
  {
    pathParams: {
      productPublicId: route.params.productId,
    },
  },
)

if (productDataError.value) {
  logger.error('[BUYBACK][API][QUOTES] Error getting product', {
    error: productDataError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

async function generateOfferPayload(
  quotationsData: RefurbisherQuotationsListing[],
): Promise<Offer> {
  return quotationsData.reduce(
    (acc, quotation) => ({
      ...acc,
      [quotation.grade.code]: {
        price: removeDecimalsFromString(quotation.price?.amount),
        sku: quotation.sku,
      },
    }),
    {},
  )
}

async function getMarketQuotations({
  showToastOnError,
  countryCode,
}: {
  showToastOnError?: boolean
  countryCode?: string
}) {
  try {
    isLoadingQuotations.value = true

    const quotationsData = await $httpFetch(getRefurbisherProductListings, {
      pathParams: {
        productPublicId: route.params.productId,
        countryCode,
      },
    })
    quotations.value = quotationsData
    offer.value = await generateOfferPayload(quotationsData)
  } catch (err) {
    if (showToastOnError) {
      openErrorToast({
        title: i18n(toastTranslations.toastErrorTitle),
        content: i18n(toastTranslations.toastErrorContent),
      })
    }

    logger.error('[BUYBACK][API][QUOTES] Error getting listings', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    isLoadingQuotations.value = false
  }
}

const activeMarket = computed({
  get: () =>
    typeof route.query.countryCode === 'string'
      ? route.query.countryCode
      : productData.value?.availableMarkets?.[0],
  set: (countryCode) => {
    if (typeof countryCode === 'string' && route.name) {
      navigateTo({
        name: route.name,
        params: route.params,
        query: { countryCode },
      })
      getMarketQuotations({ countryCode })
    }
  },
})

const breads = computed(() => [
  {
    title: i18n(translations.createListingTitle),
    link: {
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
    },
  },
  { title: productData.value?.title || '', slug: '', link: '' },
])

const currency = computed(() =>
  activeMarket.value
    ? getMarketplace(activeMarket.value as MarketCountryCode)?.market
        .defaultCurrency
    : '',
)

const listingsPayload = computed(() => {
  return Object.entries(offer.value).map(([grade, offerItem]) => {
    const priceInt = offerItem.price ? parseInt(offerItem.price, 10) : NaN
    const publicationState = !priceInt
      ? LISTING_PUBLICATION_STATE.OFFLINE
      : LISTING_PUBLICATION_STATE.ONLINE

    return {
      grade,
      sku: offerItem.sku,
      publicationState,
      ...(priceInt >= 0 ? { price: offerItem.price } : {}),
    }
  })
})

function handleGetBackbox(quotation: RefurbisherQuotationsListing) {
  if (isEmpty(quotation.backboxPrice)) return
  const backboxPrice = parseInt(quotation.backboxPrice.amount, 10)
  const price = parseInt(quotation.price?.amount, 10)
  const id = quotation.grade.code
  const canHaveBackbox = Number.isNaN(price) || price < backboxPrice
  if (canHaveBackbox) {
    offer.value[id].price = (backboxPrice + 1).toString()
  }
}

function handleGetAllBackBoxes() {
  quotations.value.forEach(handleGetBackbox)
}

async function saveListing({ redirect }: { redirect: boolean }) {
  try {
    isSavingListing.value = redirect === false
    isSavingListingWithRedirect.value = redirect === true

    await $httpFetch(putRefurbisherQuotations, {
      body: {
        countryCode: activeMarket.value,
        productId: productData.value?.productId,
        listings: listingsPayload.value,
      },
    })
    openSuccessToast({
      title: i18n(translations.toastTitle),
    })
    if (redirect) {
      navigateTo({
        name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
      })
    }
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error updating quotations', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    isSavingListing.value = false
    isSavingListingWithRedirect.value = false
  }
  await getMarketQuotations({
    countryCode: activeMarket.value,
    showToastOnError: false,
  })
}

function getHeadingClass(index: number) {
  const common = tw`h-96 flex flex-grow border-static-default-low border-b-2 items-center px-8`
  const hideDesktop = tw`md:hidden`

  return index === 0 ? [common] : [common, hideDesktop]
}

onMounted(() => {
  getMarketQuotations({ countryCode: activeMarket.value })
})
</script>
